// MessageBubble.js (updated)

import React, { useMemo, useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import Avatar from "../../../../theme/layout/partials/Avatar";
import Divider from "../../../../theme/partials/Divider";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { convertJSONtoMarkdown, isEmpty, textToClipboard } from "../../../../helpers/Functions";
import LoadingText from "../../../../theme/partials/LoadingText";
import TextView from "../../../../theme/partials/Typography";
import AIMessageFeedbackControls from "../ai-feedback/AIMessageFeedbackControls";
import EnhancedMarkdownRenderer from "./EnhancedMarkdownRenderer";
import { OTOMA_LOGO_ICON } from "../../../../models/Constants";
import useGoogle from "../../../../hooks/useGoogle";

// Lazy load Dialog components
const LazyDialog = React.lazy(() => import("@mui/material/Dialog"));
const LazyDialogTitle = React.lazy(() => import("@mui/material/DialogTitle"));
const LazyDialogContent = React.lazy(() => import("@mui/material/DialogContent"));
const LazyDialogActions = React.lazy(() => import("@mui/material/DialogActions"));
const LazyTextField = React.lazy(() => import("@mui/material/TextField"));
const LazyButton = React.lazy(() => import("@mui/material/Button"));

const MessageBubble = ({
	showMeta = false,
	messageThread = [],
	messagesEndRef,
	isSubmitting,
	partialResponse,
	runStatus = "Thinking",
	getFilesDownload,
	aiSettings,
	threadType,
	rerenderMarkdown = () => {},
	withFeedbackControls = true,
	...props
}) => {
	const [expandedStates, setExpandedStates] = useState({});
	const [showExpandButtonStates, setShowExpandButtonStates] = useState({});
	const [contentHeights, setContentHeights] = useState({});
	const [docName, setDocName] = useState("New Document");
	const [isNamingDialogOpen, setIsNamingDialogOpen] = useState(false);
	const [contentToExport, setContentToExport] = useState("");
	const [isExporting, setIsExporting] = useState(false);
	const messageRefs = useRef([]);
	const { isGoogleReady, requestAccessToken } = useGoogle();

	const openExportDialog = (markdownContent) => {
		setDocName("New Document");
		setContentToExport(markdownContent);
		setIsNamingDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setIsNamingDialogOpen(false);
		setContentToExport("");
	};

	const handleExportToGoogleDocs = async () => {
		if (!isGoogleReady || !contentToExport) {
			console.error("Google API is not ready or no content to export.");
			handleCloseDialog();
			return;
		}
		setIsExporting(true);

		let processedContent = contentToExport;

		// Find all Vega-Lite JSON blocks
		const vegaRegex = /```vega-lite\n([\s\S]*?)\n```/g;
		let matches = [...processedContent.matchAll(vegaRegex)];

		if (matches.length > 0) {
			// Lazy load vegaEmbed only when needed
			const { default: vegaEmbed } = await import("vega-embed");
			
			for (const match of matches) {
				const fullMatch = match[0];
				const jsonString = match[1];

				try {
					const spec = JSON.parse(jsonString);

					// Create a temporary, hidden div to render the chart
					const tempDiv = document.createElement('div');
					tempDiv.style.position = 'absolute';
					tempDiv.style.left = '-9999px';
					document.body.appendChild(tempDiv);

					const view = await vegaEmbed(tempDiv, spec);
					const imageUrl = await view.view.toImageURL('png');

					// Clean up the temporary div
					document.body.removeChild(tempDiv);

					// Replace the JSON block with an HTML image tag
					const imgTag = `<img src="${imageUrl}" alt="Chart" />`;
					processedContent = processedContent.replace(fullMatch, imgTag);

				} catch (error) {
					console.error("Error processing Vega-Lite JSON:", error);
					// If processing fails, leave the original JSON block
				}
			}
		}

		// No base64 conversion needed — Google's servers fetch image URLs server-side (no CORS issue)
		// Just ensure Vega-Lite base64 data URLs (already inline) are kept as-is

		try {
			const accessToken = await requestAccessToken();
			
			// Lazy load marked only when needed
			const { marked } = await import("marked");
			
			const metadata = {
				name: docName,
				mimeType: 'application/vnd.google-apps.document',
			};

			const boundary = '-------314159265358979323846';
			const delimiter = `\r\n--${boundary}\r\n`;
			const close_delim = `\r\n--${boundary}--`;

			// Custom renderer for marked - handles both old (href, title, text) and new (token object) API
			const renderer = new marked.Renderer();
			renderer.image = (hrefOrToken, title, text) => {
				const href = typeof hrefOrToken === 'object' ? hrefOrToken.href : hrefOrToken;
				const altText = typeof hrefOrToken === 'object' ? hrefOrToken.text : text;
				const titleAttr = typeof hrefOrToken === 'object' ? hrefOrToken.title : title;
				return `<img src="${href}" alt="${altText || ''}" title="${titleAttr || ''}" style="width:100%;max-width:640px;height:auto;display:block;margin:12px 0;" />`;
			};

			const htmlContent = marked(processedContent, { renderer });

			const multipartRequestBody =
				delimiter +
				'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
				JSON.stringify(metadata) +
				delimiter +
				'Content-Type: text/html\r\n\r\n' +
				htmlContent +
				close_delim;

			const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
				method: 'POST',
				headers: {
					'Authorization': `Bearer ${accessToken}`,
					'Content-Type': `multipart/related; boundary=${boundary}`,
				},
				body: multipartRequestBody,
			});

			if (response.ok) {
				const file = await response.json();
				// Optionally, open the new doc in a new tab
				window.open(`https://docs.google.com/document/d/${file.id}/edit`, '_blank');
			} else {
				console.error('Error creating file:', await response.json());
			}
		} catch (error) {
			console.error('Error during Google Docs export:', error);
		} finally {
			setIsExporting(false);
			handleCloseDialog();
		}
	};

	const handleDownloadAsPdf = async (message) => {
		// Lazy load dependencies only when PDF export is needed
		const [{ default: jsPDF }, { default: html2canvas }, { marked }] = await Promise.all([
			import("jspdf"),
			import("html2canvas"),
			import("marked")
		]);

		let markdownString = threadType === 2 ? convertJSONtoMarkdown(message.content) : message.content;

		if (!isEmpty(message.s3_url) && isImage(message.s3_url)) {
			const imageUrl = process.env.REACT_APP_S3_BUCKET + message.s3_url;
			markdownString += `\n\n![Generated content](${imageUrl})`;
		}

		// Parse markdown to HTML
		let htmlContent = marked.parse(markdownString);

		// Convert markdown images to base64 to avoid CORS issues
		const imageRegex = /!\[.*?\]\((.*?)\)/g;
		const imgMatches = [...markdownString.matchAll(imageRegex)];

		for (const match of imgMatches) {
			const originalUrl = match[1];
			try {
				// Replace CDN base URL with the local proxy path
				const proxyUrl = process.env.REACT_APP_S3_BUCKET
					? originalUrl.replace(process.env.REACT_APP_S3_BUCKET, '/lernnex-files')
					: originalUrl;
				
				const response = await fetch(proxyUrl);
				if (!response.ok) {
					console.error(`Failed to fetch image: ${proxyUrl}`);
					continue;
				}
				
				const blob = await response.blob();
				const base64String = await new Promise((resolve, reject) => {
					const reader = new FileReader();
					reader.onloadend = () => resolve(reader.result);
					reader.onerror = reject;
					reader.readAsDataURL(blob);
				});
				
				// Replace the URL in the HTML content with base64
				htmlContent = htmlContent.replace(originalUrl, base64String);
			} catch (error) {
				console.error("Error converting image to base64 for PDF:", error);
			}
		}

		// Render into a hidden div
		const tempDiv = document.createElement('div');
		tempDiv.style.position = 'absolute';
		tempDiv.style.left = '-9999px';
		tempDiv.style.top = '0';
		tempDiv.style.width = '794px';
		tempDiv.style.padding = '40px';
		tempDiv.style.background = 'white';
		tempDiv.style.color = 'black';
		tempDiv.style.fontFamily = 'Arial, sans-serif';
		tempDiv.style.fontSize = '14px';
		tempDiv.innerHTML = htmlContent;
		document.body.appendChild(tempDiv);

		// Wait for all images to load with proper error tracking
		const images = tempDiv.querySelectorAll('img');
		await Promise.all([...images].map(img => {
			if (img.complete) {
				return Promise.resolve();
			}
			return new Promise((resolve) => {
				img.onload = () => {
					// Set explicit dimensions to ensure html2canvas captures them
					if (!img.style.width && img.naturalWidth) {
						img.style.width = Math.min(img.naturalWidth, 714) + 'px'; // 794 - 80 (padding)
						img.style.height = 'auto';
					}
					resolve();
				};
				img.onerror = (err) => {
					console.error('Image failed to load:', img.src, err);
					resolve(); // Continue even if image fails
				};
			});
		}));

		try {
			const canvas = await html2canvas(tempDiv, { 
				scale: 2,
				useCORS: true,
				allowTaint: true,
				logging: false
			});
			const imgData = canvas.toDataURL('image/png');
			const doc = new jsPDF('p', 'mm', 'a4');
			const pageWidth = doc.internal.pageSize.getWidth();
			const pageHeight = doc.internal.pageSize.getHeight();
			const imgWidth = pageWidth;
			const imgHeight = (canvas.height * imgWidth) / canvas.width;

			let heightLeft = imgHeight;
			let yPosition = 0;

			doc.addImage(imgData, 'PNG', 0, yPosition, imgWidth, imgHeight);
			heightLeft -= pageHeight;

			while (heightLeft > 0) {
				yPosition -= pageHeight;
				doc.addPage();
				doc.addImage(imgData, 'PNG', 0, yPosition, imgWidth, imgHeight);
				heightLeft -= pageHeight;
			}

			doc.save('document.pdf');
		} finally {
			document.body.removeChild(tempDiv);
		}
	};

	const isImage = (fileName) => {
		if (!fileName) return false;
		const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
		const fileExtension = fileName.split('.').pop().toLowerCase();
		return imageExtensions.includes(fileExtension);
	};

	const toggleExpand = (index) => {
		setExpandedStates(prev => ({
			...prev,
			[index]: !prev[index]
		}));
	};

	const latestUserIndex = useMemo(() => {
		for (let i = messageThread.length - 1; i >= 0; i--) {
			if (messageThread[i].role === "user") return i;
		}
		return -1;
	}, [messageThread]);

	// Measure content heights and compute if minimized view would overflow for user messages
	useEffect(() => {
		messageRefs.current.forEach((ref, index) => {
			if (!ref) return;
			const message = messageThread[index];
			if (!message) return;

			// Record full content height for expansion without scroll
			setContentHeights(prev => ({
				...prev,
				[index]: ref.scrollHeight || 0,
			}));

			// Only user messages can be minimized/expanded
			const isUserMsg = message.role !== 'assistant';
			const isImgMsg = isImage(message?.file_name);
			let hasOverflowMin = false;
			if (isUserMsg) {
				// Minimized thresholds: text 18vh, images 60vh
				const thresholdPx = Math.floor((isImgMsg ? 0.60 : 0.18) * window.innerHeight);
				hasOverflowMin = (ref.scrollHeight || 0) > thresholdPx;
			}
			setShowExpandButtonStates(prev => ({
				...prev,
				[index]: hasOverflowMin,
			}));
		});
	}, [messageThread]);

	return messageThread.map((message, index) => {
		const isLastMessage = index === messageThread.length - 1;
		const isLatestUser = index === latestUserIndex;
		const isExpanded = expandedStates[index];
		const isUserMessage = message.role !== "assistant";
		const mustExpand = showExpandButtonStates[index];
		const isImageMessage = isImage(message?.file_name)

		// Heights for user messages: no scrollbars, hide overflow when minimized
		const minimizedHeight = isImageMessage ? '60vh' : '18vh';
		const containerMaxHeight = isUserMessage
			? (isExpanded ? undefined : minimizedHeight)
			: undefined;
		const containerOverflowY = 'hidden';

		return (
			<Box key={index}>
				<Box
					ref={(el) => {
						if (isLatestUser && messagesEndRef) {
							messagesEndRef.current = el;
						}
					}}
					className={`d-flex ${isUserMessage ? "justify-content-end" : "justify-content-start"
						} ${isUserMessage ? "align-items-center" : "align-items-start"} mb-3`
					}
				>
					{!isUserMessage && (
						<Box className="d-flex flex-column align-items-start py-3 h-100">
							<Avatar 
								lib_img={OTOMA_LOGO_ICON} 
								customclasses="ms-3"
								transparentBg={true}
							/>
						</Box>
					)}
					<Box
						className={`my-2 ${isUserMessage ? "user-chat-container bubble-chat-container" : "ai-chat-container"
							}`}
					>
						<Box
							ref={el => messageRefs.current[index] = el}
							sx={{
								paddingX: {
									xm: 0,
									md: isUserMessage ? "1rem" : "2vw",
								},
								overflowX: 'auto',
								minHeight: '2.5rem',
								wordWrap: 'break-word',
									maxHeight: isUserMessage ? containerMaxHeight : undefined,
									overflowY: isUserMessage ? containerOverflowY : 'visible',
								maxWidth: '80vw',
							}}
						>
							{
								(message?.role === "content" && message.file_name) ? (
									isImageMessage ? (
										<img
											src={process.env.REACT_APP_S3_BUCKET + message.file_location}
											alt={message.file_name}
											style={{ maxWidth: '25vw', minWidth: '500px', height: 'auto', borderRadius: '8px' }}
										/>
									) : (
										<a className="color-primary" href={process.env.REACT_APP_S3_BUCKET + message.file_location} target="_blank" rel="noopener noreferrer">
											{message.file_name}
										</a>
									)
								) : (
									<div className="ai-response" id={`ai-response-${index}`}>
										<EnhancedMarkdownRenderer 
											content={
												(threadType === 2)
													? convertJSONtoMarkdown(message.content)
													: message.content
											}
										/>
										{
											(!isEmpty(message.s3_url)) && (
												isImage(message.s3_url) ? (
													<img
														src={process.env.REACT_APP_S3_BUCKET + message.s3_url}
														alt="Generated content"
														style={{ maxWidth: '25vw', minWidth: '500px', height: 'auto', borderRadius: '8px', marginTop: '1rem' }}
													/>
												) : (
													<a 
														className="color-primary" 
														href={process.env.REACT_APP_S3_BUCKET + message.s3_url} 
														target="_blank" 
														rel="noopener noreferrer"
														style={{ display: 'block', marginTop: '1rem' }}
													>
														{message.s3_url.split('/').pop()}
													</a>
												)
											)
										}
									</div>
								)
							}
						</Box>
						{showMeta && (
							(() => {
								if (message?.role === "assistant") return null;

								const createdAt = message.createdAt || message.created_at;
								const inputTokens = message.token_prompt ?? message.token_prompt;
								const outputTokens = message.token_completion ?? message.token_completion;
								const totalTokens = message.token_total ?? message.token_total ?? (typeof inputTokens === 'number' && typeof outputTokens === 'number' ? inputTokens + outputTokens : undefined);

								return (
									<Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'end', gap: 1 }}>
										<TextView fontcolor="secondary" fontsize="small">
											{new Date(createdAt).toLocaleTimeString()}
										</TextView>
									</Box>
								);
							})()
						)}
						{isUserMessage && (mustExpand || !isEmpty(message?.file_ids)) && (
							<Box sx={{ borderTop: "1px outset black" }} className="d-flex flex-column align-items-center">
								<Box
									className={
										(mustExpand && !isEmpty(message?.file_ids))
											? "w-100 d-flex justify-content-around"
											: "w-100 d-flex justify-content-center" 
									}
								>
									{mustExpand && (
										<IconButton
											onClick={() => toggleExpand(index)}
											title={isExpanded ? "Minimize" : "Expand"}
											className="font-size-large" 
											sx={{ color: 'gray' }}
										>
											{isExpanded ? (
												<>Show Less <ExpandLessIcon sx={{ color: 'gray' }} /></>
											) : (
												<>Show More <ExpandMoreIcon sx={{ color: 'gray' }} /></>
											)}
										</IconButton>
									)}
									{!isEmpty(message?.file_ids) && (
										<TextView
											className={`
											${mustExpand
													? "ms-2"
													: "w-100 text-center"
												}
											mt-2 clickable-text
										`}
											onClick={(e) => {
												getFilesDownload(JSON.stringify(message?.file_ids))
											}}
										>
											Show Files
										</TextView>
									)}
								</Box>
							</Box>
						)}
					</Box>

					{isUserMessage && (
						<Box className="d-flex flex-column align-items-start py-3 h-100">
							<Avatar
								userdetail={props.user_details}
								customclasses="ms-3"
							/>
						</Box>
					)}
				</Box>

				{!isUserMessage && (
					<>
						<Divider className="px-3" />
						<Box className="d-flex justify-content-end align-items-center my-2">
							{withFeedbackControls && (
								<AIMessageFeedbackControls 
									messageId={message?.message_id} 
									activeType={message?.user_feedback?.feedback_type}
								/>
							)}
							<IconButton title="Copy">
								<ContentCopyIcon onClick={() => textToClipboard(message.content, "Message Copied.")} />
							</IconButton>
							<IconButton title="Download as PDF" onClick={() => handleDownloadAsPdf(message, index)}>
								<PictureAsPdfIcon />
							</IconButton>
							<IconButton
								title="Export to Google Docs"
								onClick={() => {
									let exportContent = (threadType === 2)
										? convertJSONtoMarkdown(message.content)
										: message.content;

									if (!isEmpty(message.s3_url) && isImage(message.s3_url)) {
										const imageUrl = process.env.REACT_APP_S3_BUCKET + message.s3_url;
										// Append the image markdown to the content
										exportContent += `\n\n![Generated content](${imageUrl})`;
									}
									openExportDialog(exportContent);
								}}
								disabled={!isGoogleReady}
							>
								<DescriptionIcon />
							</IconButton>
						</Box>
					</>
				)}
				{(isSubmitting && isLastMessage) && (
					<Box className="d-flex justify-content-start my-2 px-5">
						<LoadingText
							title={runStatus}
							color="black"
						/>
					</Box>
				)}
				{isNamingDialogOpen && (
					<React.Suspense fallback={<Box />}>
						<LazyDialog open={isNamingDialogOpen} onClose={handleCloseDialog}>
							<LazyDialogTitle>Name Your Document</LazyDialogTitle>
							<LazyDialogContent>
								<LazyTextField
									autoFocus
									margin="dense"
									id="name"
									label="Document Name"
									type="text"
									fullWidth
									variant="standard"
									defaultValue={docName}
									onChange={(e) => setDocName(e.target.value)}
								/>
							</LazyDialogContent>
							<LazyDialogActions>
								<LazyButton onClick={handleCloseDialog}>Cancel</LazyButton>
								<LazyButton onClick={handleExportToGoogleDocs} disabled={isExporting}>
									{isExporting ? "Exporting..." : "Export"}
								</LazyButton>
							</LazyDialogActions>
						</LazyDialog>
					</React.Suspense>
				)}
			</Box>
		);
	});
};

export default MessageBubble;